import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, useProjects, useTeams } from '../hooks';
import { ProjectsSection, ProjectForm } from '../components/project';
import { RoleBadge } from '../components/teams';
import { Alert, EmptyState, Loading, Modal, SessionUnavailable } from '../components/common';
import { Skeleton } from '../components/Skeleton';
import { getApiBaseUrl } from '../config';
import { ROUTES } from '../routes';
import type { TeamSummary } from '@mockd/shared';

export function TeamDashboard() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, sessionError } = useAuth();
  const { getTeam } = useTeams();
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
    fetchTeamProjects,
    createTeamProject,
  } = useProjects();

  const [team, setTeam] = useState<TeamSummary | null>(null);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [endpointCounts, setEndpointCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!authLoading && !user && !sessionError) {
      navigate('/login', { replace: true, state: { from: { pathname: `/teams/${teamId}` } } });
    }
  }, [user, authLoading, sessionError, teamId, navigate]);

  useEffect(() => {
    if (!teamId || !user) return;
    setTeamLoading(true);
    getTeam(teamId)
      .then((t) => {
        setTeam(t);
        setTeamError(null);
      })
      .catch((err) => {
        setTeamError(err instanceof Error ? err.message : 'Failed to load team');
      })
      .finally(() => setTeamLoading(false));
  }, [teamId, user, getTeam]);

  useEffect(() => {
    if (teamId && user) fetchTeamProjects(teamId);
  }, [teamId, user, fetchTeamProjects]);

  const fetchEndpointCounts = useCallback(async (projectIds: string[]) => {
    const counts: Record<string, number> = {};
    await Promise.all(
      projectIds.map(async (projectId) => {
        try {
          const response = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}/endpoints`, {
            credentials: 'include',
          });
          if (response.ok) {
            const json = await response.json();
            counts[projectId] = (json.data || []).length;
          }
        } catch {
          // Leave the count unknown — the card omits it rather than showing 0
        }
      })
    );
    setEndpointCounts(counts);
  }, []);

  useEffect(() => {
    if (projects.length > 0) fetchEndpointCounts(projects.map((p) => p.id));
  }, [projects, fetchEndpointCounts]);

  const handleCreateProject = async (data: { name: string; subdomain: string }) => {
    if (!teamId) return;
    setIsCreating(true);
    try {
      const project = await createTeamProject(teamId, data);
      setShowNewProjectModal(false);
      navigate(`/projects/${project.id}`);
    } finally {
      setIsCreating(false);
    }
  };

  if (!authLoading && sessionError) {
    return <SessionUnavailable />;
  }

  if (authLoading || !user || !teamId || teamLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (teamError || !team) {
    return (
      <div className="min-h-screen bg-base-200">
        <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <EmptyState
            as="h1"
            title="Team not found"
            description={teamError || "This team doesn't exist, or you're not a member."}
            action={
              <Link to={ROUTES.dashboard()} className="btn btn-primary btn-sm">
                Go to your projects
              </Link>
            }
          />
        </main>
      </div>
    );
  }

  const canCreate = team.role !== 'viewer';
  return (
    <div className="min-h-screen bg-base-200">
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <Modal
          isOpen={showNewProjectModal}
          onClose={() => setShowNewProjectModal(false)}
          title="New Project"
          boxClassName="max-w-4xl"
          showCloseButton
        >
          <ProjectForm
            onSubmit={handleCreateProject}
            onCancel={() => setShowNewProjectModal(false)}
            isLoading={isCreating}
          />
        </Modal>

        <div className="flex items-center justify-between mb-6">
          <h1 className="flex items-center gap-2">
            {team.name}
            <RoleBadge role={team.role} />
          </h1>
          {/* Visible to every member — the settings page itself gates individual
              actions (invites, edits, delete) down to owner/admin. */}
          <Link to={ROUTES.teams.settings(team.id)} className="btn btn-ghost btn-sm">
            Team settings
          </Link>
        </div>

        {projectsError && (
          <Alert variant="error" className="mb-8">{projectsError}</Alert>
        )}

        {projectsLoading && projects.length === 0 ? (
          <Skeleton variant="project-list" />
        ) : (
          <ProjectsSection
            projects={projects}
            endpointCounts={endpointCounts}
            canCreate={canCreate}
            onCreateClick={() => setShowNewProjectModal(true)}
            isCreating={isCreating}
          />
        )}
      </main>
    </div>
  );
}
